import { useTenantRealmDropdownsFlow } from 'TenantModule/hooks/useTenantRealmDropdownsFlow';
import { FormEvent, MouseEventHandler, useRef, useState } from 'react';
import { useAddServiceToRealm } from 'RealmModule/hooks/useAddServiceToRealm';
import { IntegrationAgentNames } from '@constants';
import { Realm } from 'types';

export interface AddServiceToRealmFields {
  integrationAgentName: Nullable<IntegrationAgentNames>;
  isFirstRealm: boolean;
  adminDatabaseCredentialSecret: string;
}

export const useAddServiceToRealmForm = ({
  realmId,
  onSuccess,
}: {
  realmId?: Realm['realm_id'];
  /** Called after the service is successfully added to the realm. */
  onSuccess?: VoidFunction;
} = {}) => {
  const { selectedTenant, onSelectRealm, selectedRealm, onSelectTenantRealm, isFetchingSandboxes } =
    useTenantRealmDropdownsFlow({ realmId });
  const realmDropdownRef = useRef<HTMLDivElement>(null);
  const [addRealmServiceParams, setAddRealmServiceParams] = useState<AddServiceToRealmFields>({
    integrationAgentName: null,
    isFirstRealm: false,
    adminDatabaseCredentialSecret: '',
  });
  // Hook-level so the success callback survives this form unmounting while
  // the request is in flight (see useAddServiceToRealm).
  const { addServiceToRealm, isAddingServiceToRealm } = useAddServiceToRealm({
    onSuccess,
  });

  const onRealmSelectorClick: MouseEventHandler<HTMLElement> = (e) => {
    if (selectedTenant) {
      onSelectRealm({
        target: e.currentTarget,
        tenantId: selectedTenant.id,
      });
    }
  };

  const onTenantSelectorClick: MouseEventHandler<HTMLElement> = (e) => {
    if (e.currentTarget && realmDropdownRef.current) {
      onSelectTenantRealm({
        tenantDropdownTarget: e.currentTarget,
        realmDropdownTarget: realmDropdownRef.current,
      });
    }
  };

  const onAddRealmServiceFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, type } = event.target;

    if (type === 'checkbox') {
      const checked = event.target.checked;
      setAddRealmServiceParams((prevParams) => ({
        ...prevParams,
        [name]: checked,
      }));
    } else {
      const value = event.target.value;

      setAddRealmServiceParams((prevParams) => ({
        ...prevParams,
        [name]: value,
      }));
    }
  };

  const onSubmitRealmAddServiceForm = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // A pinned realm (realmId prop) may belong to a different tenant than the
    // globally selected one, so the realm's own tenant wins.
    const tenantName = selectedRealm?.tenant || selectedTenant?.tenantName;
    if (selectedRealm && tenantName && addRealmServiceParams.integrationAgentName) {
      const params = {
        tenant: tenantName,
        ...(addRealmServiceParams.isFirstRealm && {
          isFirstRealm: addRealmServiceParams.isFirstRealm,
        }),
        ...(addRealmServiceParams.adminDatabaseCredentialSecret && {
          adminDatabaseCredentialSecret: addRealmServiceParams.adminDatabaseCredentialSecret,
        }),
        requestingService: addRealmServiceParams.integrationAgentName,
        realmId: selectedRealm.realm_id,
      };
      addServiceToRealm(params);
    }
  };

  return {
    realmDropdownRef,
    selectedRealm,
    selectedTenant,
    isFetchingSandboxes,
    onRealmSelectorClick,
    onTenantSelectorClick,
    addRealmServiceParams,
    onAddRealmServiceFormChange,
    onSubmitRealmAddServiceForm,
    isAddingServiceToRealm,
  };
};
